import type { ReactElement } from "react";
import { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useParams } from "react-router-dom";
import { Button } from "../button";
import { Image } from "../image";
import { Html } from "../html";
import { useOpenNewsArticle } from "../../hooks/use-open-news-article";
import { SwiperCarousel } from "../swiper-carousel";
import { useSendCommand } from "../../hooks/use-send-command";
import { BRIDGE_FUNCTION, OPEN_LINK } from "../../constants/types/commands";

import styles from "./styles.modules.scss";
import {
    FullscreenEntryContextProvider,
    useFullscreenEntryContext,
} from "../../stores/fullscreenEntries";
import { useSnowplowContext } from "../../analytics/snowplow";
import { Cta } from "../esport/types";

type FullscreenEntryContainerProps = {
    children: ReactElement;
    tab: "news" | "community" | "esport";
    entries: InboxFullscreenEntry[];
};

function FullscreenEntryCarouselSlide(slide: InboxFullscreenEntrySlide) {
    const isPortrait = window.innerWidth < window.innerHeight;

    const [isFullHeight, setIsFullheight] = useState(false);

    const onClick = (e: React.MouseEvent<HTMLElement>) => {
        const el = e.target as HTMLElement;

        if (el.tagName === "A") {
            return;
        }

        if (isFullHeight) {
            return;
        }

        if (!isPortrait) {
            return;
        }

        setIsFullheight(!isFullHeight);
    };

    const style = isPortrait
        ? {
              maxHeight: isFullHeight ? "80vh" : "30vh",
          }
        : {};

    return (
        <div className={styles.slide}>
            {slide.backgroundMedia && <Image sources={slide.backgroundMedia} />}

            {slide.body && (
                <div
                    className={`${styles.prompt} prompt`}
                    onClick={onClick}
                    style={style}
                >
                    <Html html={slide.body} />
                </div>
            )}
        </div>
    );
}

function FullscreenEntryCarousel(props: {
    entry: InboxFullscreenEntry;
    locale: { lang: string };
    closeEntry: (entryId: string) => void;
}) {
    const { entry, closeEntry, locale } = props;

    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();

    const onLinkClick = (cta: Cta) => {
        if (cta.urlType === "article") {
            const page =
                entry.type === "esportFullscreenCarousel" ? "esport" : "news";

            openNewsArticle(cta.url.id, "", page);

            closeEntry(entry.id);
            return;
        }
        if (cta.urlType === "bridgeFunction") {
            sendCommand(BRIDGE_FUNCTION, {
                functionName: cta.functionName,
                functionArgs: cta.functionArgs,
            });
            return;
        }

        sendCommand(OPEN_LINK, {
            url: cta.url,
            id: cta.id,
            label: cta.label,
        });
        closeEntry(entry.id);
    };

    const cta = entry.readMoreButton;

    return (
        <div className={`${styles.carousel} fullscreen-carousel`}>
            <div className={`${styles.buttons} buttons`}>
                {cta && (
                    <Button
                        variant={cta.variant}
                        onClick={() => onLinkClick(cta)}
                    >
                        <span className="buttonTextWrapper">{cta.label}</span>
                    </Button>
                )}

                <div data-testid="CLOSE_FULLSCREEN_ENTRY">
                    <Button
                        onClick={() => {
                            closeEntry(entry.id);
                        }}
                    >
                        <span className="buttonTextWrapper">
                            <FormattedMessage id={"globalText.toNews"} />
                        </span>
                    </Button>
                </div>
            </div>

            <SwiperCarousel
                locale={locale}
                Slide={FullscreenEntryCarouselSlide}
                slides={entry.slides}
            />
        </div>
    );
}

const FullscreenEntryModal = ({
    entries,
    tab,
    ...viewProps
}: {
    entries: InboxFullscreenEntry[];
    tab: "news" | "community" | "esport";
}) => {
    const { state, actions } = useFullscreenEntryContext();
    const { trackImpression } = useSnowplowContext();

    const params = useParams();
    const locale = params?.lang ? { lang: params.lang } : { lang: "en" };

    // There might be multiple entries, but we only want to show one per opening of the inbox
    const [hasShownOneEntryThisSession, setHasShownOneEntryThisSession] =
        useState(false);

    const closeEntry = (entryId: string) => {
        actions.closeFullscreenEntry(entryId);
        setHasShownOneEntryThisSession(true);
    };

    const activeEntry = entries.find(
        (entry) => entry.id === state.activeEntryId,
    );

    useEffect(() => {
        if (activeEntry) {
            // This is an ugly hack to prevent the poll modal from resetting this state... Will be fixed in the future
            setTimeout(() => {
                document.body.style.overflow = "hidden";
            }, 50);
        } else {
            document.body.style.overflow = "initial";
        }
    }, [activeEntry]);

    useEffect(() => {
        if (state.activeEntryId) {
            trackImpression(
                state.activeEntryId,
                `Fullscreen Entry - ${state.activeEntryId}`,
            );
        }
    }, [state.activeEntryId]);

    const modalRootEl = document.getElementById("modal-root");

    if (!modalRootEl) {
        return null;
    }

    if (hasShownOneEntryThisSession) {
        return null;
    }

    if (!activeEntry) {
        return null;
    }

    const contents = (
        <div
            className={`${styles.fullscreen} fullscreen-entry ${tab ?? ""}`}
            {...viewProps}
        >
            <FullscreenEntryCarousel
                closeEntry={closeEntry}
                entry={activeEntry}
                locale={locale}
            />
        </div>
    );

    return ReactDOM.createPortal(contents, modalRootEl);
};

export function FullscreenEntryContainer(
    props: FullscreenEntryContainerProps,
): ReactElement {
    const entryIds = props.entries.map((entry) => entry.id);

    return (
        <FullscreenEntryContextProvider availableEntryIds={entryIds}>
            <FullscreenEntryModal tab={props.tab} entries={props.entries} />

            {props.children}
        </FullscreenEntryContextProvider>
    );
}
